import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { calculateTriangleArea, getDistanceFromLine, isSegmentIntersecting, scaledCoordinate } from '../drawing.helper'

export type HeadAndShoulderPatternState = {
    a?: Point,
    b?: Point,
    c?: Point,
    d?: Point,
    e?: Point,
    f?: Point,
    g?: Point
}

export type HeadAndShoulderPatternSettings = {
    fillColor: ColorValue,
    lineColor: ColorValue,
    lineWidth: number,
    point1: Point,
    point2: Point,
    point3: Point,
    point4: Point,
    point5: Point,
    point6: Point,
    point7: Point
}


const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

class HeadAndShoulderPatternTool extends DrawingBase<any, HeadAndShoulderPatternSettings, HeadAndShoulderPatternState> {
    scaledCoordinates: Record<string, PointCoordinates> = {}
    lengths: Record<string, number> = {}
    slopes: Record<string, number> = {}
    intercepts: Record<string, number> = {}
    isLeftIntersecting = false
    isRightIntersecting = false

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { a, b, c, d, e, f, g, p1, p2, l, r } = this.scaledCoordinates

        let prevPoint: PointCoordinates

        ctx.beginPath()
        this.setLineAttributes(ctx)
        const points = [a, b, c, d, e, f, g]
        points.forEach((p, i) => {
            if (!p) return

            if (!prevPoint) {
                ctx.moveTo(p.x, p.y)
            } else {
                ctx.lineTo(p.x, p.y)
            }
            prevPoint = p
            ctx.globalAlpha = this._settings.lineColor.opacity
            ctx.stroke()
        })

        if (p1 && p2) {
            ctx.moveTo(p1.x, p1.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.globalAlpha = this._settings.lineColor.opacity
            this.setLineAttributes(ctx, true)
            ctx.stroke()
        }
        ctx.closePath()

        if (c && e) {
            this.drawTriangle(ctx, c, d, e)

            if (this.isLeftIntersecting && l) {
                this.drawTriangle(ctx, l, b, c)
            }

            if (this.isRightIntersecting && f && r) {
                this.drawTriangle(ctx, e, f, r)
            }
        }

        this.drawPointsLabels(ctx)

    };

    drawTriangle(ctx: CanvasRenderingContext2D, p1: PointCoordinates, p2: PointCoordinates, p3: PointCoordinates) {
        ctx.beginPath()
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.lineTo(p3.x, p3.y)
        ctx.lineTo(p1.x, p1.y)
        ctx.closePath()
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fillStyle = this._settings.fillColor.color
        ctx.fill()
    }

    drawPointsLabels(ctx: CanvasRenderingContext2D) {
        const { b, d, f } = this.scaledCoordinates

        const points: Array<[PointCoordinates, string]> = [
            [b, 'Left Shoulder'],
            [d, 'Head'],
            [f, 'Right Shoulder']
        ]
        points.forEach(([p, text], i) => {
            if (!p) return
            const { x, y } = p
            const necklineY = this.slopes.neckline ? x * this.slopes.neckline + this.intercepts.neckline : undefined
            const position = !necklineY || necklineY > y ? 1 : -1
            ctx.font = 12 * this.verticalPixelRatio + 'px sans-serif'
            ctx.textBaseline = 'top'
            ctx.globalAlpha = 1
            const padding = 4 * window.devicePixelRatio
            const dim = ctx.measureText(text)
            const textWidth = dim.width
            const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
            const startX = x - textWidth / 2
            const startY = y - (position > 0 ? textHeight + 2 * padding : -2 * padding)

            ctx.fillStyle = this._settings.lineColor.color
            ctx.beginPath()
            ctx.roundRect(startX - padding, startY - padding, textWidth + 2 * padding, textHeight + 2 * padding + 2 * window.devicePixelRatio, 3 * this.horizontalPixelRatio)
            ctx.closePath()
            ctx.fill()
            ctx.fillStyle = '#fff'
            ctx.fillText(text, startX, startY)
        })
    }

    calculateLineAttributes() {
        const { a, b, c, d, e, f, g } = this._state

        let prevPoint: Point;
        [a, b, c, d, e, f, g].forEach((p) => {
            if (!p) return
            let x = this.timeToCoordinate(p.x)
            let y = this.priceToCoordinate(p.y)
            let currentCoordinates: PointCoordinates = { x: 0, y: 0 }
            if (x && y) {
                currentCoordinates = {
                    x: scaledCoordinate(x, this.horizontalPixelRatio),
                    y: scaledCoordinate(y, this.verticalPixelRatio)
                }

                this.scaledCoordinates[p.id] = currentCoordinates

                if (prevPoint) {
                    const key = `${prevPoint.id}${p.id}`
                    this.lengths[key] = Math.abs(p.y - prevPoint.y)
                    const prevCoords = this.scaledCoordinates[prevPoint.id]
                    const slope = (currentCoordinates.y - prevCoords.y) / (currentCoordinates.x - prevCoords.x)
                    const intercept = currentCoordinates.y - slope * currentCoordinates.x
                    this.slopes[key] = slope
                    this.intercepts[key] = intercept
                }
            }
            prevPoint = p
        })
        const { a: as, b: bs, c: cs, e: es, f: fs, g: gs } = this.scaledCoordinates
        if (cs && es) {
            const oppositeMode = es.x < cs.x
            this.slopes.neckline = (es.y - cs.y) / (es.x - cs.x)
            this.intercepts.neckline = cs.y - this.slopes.neckline * cs.x
            const { neckline } = this.slopes
            const { neckline: neckline_i } = this.intercepts
            this.scaledCoordinates.p1 = {
                x: !oppositeMode ? 0 : this.canvasWidth,
                y: !oppositeMode ? neckline_i : this.canvasWidth * neckline + neckline_i
            }


            const lx = (this.intercepts.neckline - this.intercepts.ab) / (this.slopes.ab - this.slopes.neckline)
            const ly = lx * this.slopes.neckline + this.intercepts.neckline
            this.scaledCoordinates.l = {
                x: lx,
                y: ly
            }

            const isLeftIntersecting = isSegmentIntersecting(as.x, as.y, bs.x, bs.y, this.slopes.neckline, this.intercepts.neckline)

            this.isLeftIntersecting = isLeftIntersecting

            if (isLeftIntersecting) {
                if (!oppositeMode) {
                    this.scaledCoordinates.p1.x = Math.min(cs.x, Math.max(0, lx))
                } else {
                    this.scaledCoordinates.p1.x = Math.max(cs.x, Math.min(this.canvasWidth, lx))
                }

                this.scaledCoordinates.p1.y = this.scaledCoordinates.p1.x * this.slopes.neckline + this.intercepts.neckline
            }


            this.scaledCoordinates.p2 = {
                x: !oppositeMode ? this.canvasWidth : 0,
                y: !oppositeMode ? this.canvasWidth * neckline + neckline_i : neckline_i
            }

            if (fs && gs) {
                const rx = (this.intercepts.neckline - this.intercepts.fg) / (this.slopes.fg - this.slopes.neckline)
                const ry = rx * this.slopes.neckline + this.intercepts.neckline
                this.scaledCoordinates.r = {
                    x: rx,
                    y: ry
                }

                const isRightIntersecting = isSegmentIntersecting(fs.x, fs.y, gs.x, gs.y, this.slopes.neckline, this.intercepts.neckline)
                this.isRightIntersecting = isRightIntersecting

                if (isRightIntersecting) {
                    if (!oppositeMode) {
                        this.scaledCoordinates.p2.x = Math.max(es.x, Math.min(this.canvasWidth, rx))
                    } else {
                        this.scaledCoordinates.p2.x = Math.min(es.x, Math.max(0, rx))
                    }

                    this.scaledCoordinates.p2.y = this.scaledCoordinates.p2.x * this.slopes.neckline + this.intercepts.neckline
                }
            }
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = false) {
        if (setLineType) {
            const dotWidth = Math.max(2, this._settings.lineWidth) * window.devicePixelRatio
            let lineDash: number[] = [2 * dotWidth, dotWidth]
            ctx.setLineDash(lineDash)
        }
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
        ctx.strokeStyle = this._settings.lineColor.color
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { a, b, c, d, e, f, g, l, r } = this.scaledCoordinates
        if (!a || !b || !c || !d || !e || !f || !g) return null

        const d1 = getDistanceFromLine(scaledX, scaledY, this.slopes.ab, this.intercepts.ab)
        const d2 = getDistanceFromLine(scaledX, scaledY, this.slopes.bc, this.intercepts.bc)
        const d3 = getDistanceFromLine(scaledX, scaledY, this.slopes.cd, this.intercepts.cd)
        const d4 = getDistanceFromLine(scaledX, scaledY, this.slopes.de, this.intercepts.de)
        const d5 = getDistanceFromLine(scaledX, scaledY, this.slopes.ef, this.intercepts.ef)
        const d6 = getDistanceFromLine(scaledX, scaledY, this.slopes.fg, this.intercepts.fg)
        if (d1 <= THRESHOLD || d2 <= THRESHOLD || d3 <= THRESHOLD || d4 <= THRESHOLD || d5 <= THRESHOLD || d6 <= THRESHOLD) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        if (this.isLeftIntersecting && this.isWithinTriangle(scaledX, scaledY, l, b, c)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        if (this.isWithinTriangle(scaledX, scaledY, c, d, e)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        if (this.isRightIntersecting && this.isWithinTriangle(scaledX, scaledY, e, f, r)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    isWithinTriangle(x: number, y: number, p1: PointCoordinates, p2: PointCoordinates, p3: PointCoordinates) {
        const area = calculateTriangleArea(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)
        const area1 = calculateTriangleArea(p1.x, p1.y, p2.x, p2.y, x, y)
        const area2 = calculateTriangleArea(p2.x, p2.y, p3.x, p3.y, x, y)
        const area3 = calculateTriangleArea(p1.x, p1.y, p3.x, p3.y, x, y)

        return Number(area.toFixed(1)) === Number((area1 + area2 + area3).toFixed(1))
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'a',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.a = point
        } else if (this.lastAddedPoint.id == 'a') {
            point.id = 'b'
            this._state.b = point
        } else if (this.lastAddedPoint.id == 'b') {
            point.id = 'c'
            this._state.c = point
        } else if (this.lastAddedPoint.id == 'c') {
            point.id = 'd'
            this._state.d = point
        } else if (this.lastAddedPoint.id == 'd') {
            point.id = 'e'
            this._state.e = point
        } else if (this.lastAddedPoint.id == 'e') {
            point.id = 'f'
            this._state.f = point
        } else {
            point.id = 'g'
            this._state.g = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const { a, b, c, d, e, f, g } = this._state

        return [a!, b!, c!, d!, e!, f!, g!].filter(p => p)
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'a':
                    this._state.a = h
                    break
                case 'b':
                    this._state.b = h
                    break
                case 'c':
                    this._state.c = h
                    break
                case 'd':
                    this._state.d = h
                    break
                case 'e':
                    this._state.e = h
                    break
                case 'f':
                    this._state.f = h
                    break
                case 'g':
                    this._state.g = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        if (!this._state[id as keyof typeof this._state]) return
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { a, b, c, d, e, f, g } = this._state
        const points = [a, b, c, d, e, f, g].filter(v => v)
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => a!.y - b!.y)
        const configs: LabelAxisViewConfig<number>[] = [
            {
                id: 'outer',
                p1: sorted[0]!.y,
                p2: sorted[sorted.length - 1]!.y,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'price-' + sorted[c]!.id,
                p1: sorted[c]!.y,
                p2: sorted[c]!.y,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }
        return configs
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { a, b, c, d, e, f, g } = this._state
        const points = [a, b, c, d, e, f, g].filter(v => v)
        if (points.length < 2) return []
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => (a!.x as number) - (b!.x as number))
        const configs: LabelAxisViewConfig<Time>[] = [
            {
                id: 'time',
                p1: sorted[0]!.x,
                p2: sorted[sorted.length - 1]!.x,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'time-' + sorted[c]!.id,
                p1: sorted[c]!.x,
                p2: sorted[c]!.x,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }

        return configs
    }

    getAvailableSettings = (): DrawingSettingGroup<HeadAndShoulderPatternSettings, SettingValues>[] => {

        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#089981',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#089981',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },

                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'a',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point2',
                    label: '#2',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'b',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point3',
                    label: '#3',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'c',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point4',
                    label: '#4',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'd',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point5',
                    label: '#5',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'e',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point6',
                    label: '#6',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'f',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point7',
                    label: '#7',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'g',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    onSettingsUpdate = (settings: Partial<HeadAndShoulderPatternSettings>) => {
        if (settings.point1) {
            this._state.a = settings.point1
        }
        if (settings.point2) {
            this._state.b = settings.point2
        }
        if (settings.point3) {
            this._state.c = settings.point3
        }
        if (settings.point4) {
            this._state.d = settings.point4
        }
        if (settings.point5) {
            this._state.e = settings.point5
        }
        if (settings.point6) {
            this._state.f = settings.point6
        }
        if (settings.point7) {
            this._state.g = settings.point7
        }
    }

    getSettingsValues(): HeadAndShoulderPatternSettings {
        const settings = { ...this._settings }
        if (this._state.a) {
            settings.point1 = this._state.a
        }
        if (this._state.b) {
            settings.point2 = this._state.b
        }
        if (this._state.c) {
            settings.point3 = this._state.c
        }
        if (this._state.d) {
            settings.point4 = this._state.d
        }
        if (this._state.e) {
            settings.point5 = this._state.e
        }
        if (this._state.f) {
            settings.point6 = this._state.f
        }
        if (this._state.g) {
            settings.point7 = this._state.g
        }
        return settings
    }

    getDrawingType(): DrawingTypes {
        return 'headAndShoulderPattern'
    }

    getMiniSettings = () => {
        return ['fillColor', 'lineColor', 'lineWidth'] as (keyof HeadAndShoulderPatternSettings)[]
    }
}

export default HeadAndShoulderPatternTool